import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Landmark, Pencil, Trash2, Star } from 'lucide-react';
import type { IBankAccountCardProps } from '@/@types/invoice';

function maskAccountNumber(accountNumber: string): string {
  if (accountNumber.length <= 4) return accountNumber;
  return '****' + accountNumber.slice(-4);
}

export function BankAccountCard({
  account,
  onEdit,
  onDelete,
  onSetPrimary,
  isOrgOwner,
}: IBankAccountCardProps & { isOrgOwner: boolean }) {
  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="rounded-lg bg-muted p-2">
              <Landmark className="w-4 h-4 text-muted-foreground" />
            </div>
            {account.bankName}
          </CardTitle>
          {account.isPrimary && (
            <Badge
              className="!border-0 !bg-yellow-100 !text-yellow-800 dark:!bg-yellow-900/30 dark:!text-yellow-400"
              variant="outline"
            >
              Primary
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-sm">
          <span className="text-muted-foreground">Account Holder: </span>
          <span className="text-foreground">{account.accountHolderName}</span>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">Account #: </span>
          <span className="text-foreground font-mono">
            {maskAccountNumber(account.accountNumber)}
          </span>
        </div>
        {account.iban && (
          <div className="text-sm">
            <span className="text-muted-foreground">IBAN: </span>
            <span className="text-foreground font-mono">{account.iban}</span>
          </div>
        )}
        {account.swiftCode && (
          <div className="text-sm">
            <span className="text-muted-foreground">SWIFT: </span>
            <span className="text-foreground font-mono">{account.swiftCode}</span>
          </div>
        )}
        <div className="text-sm">
          <span className="text-muted-foreground">Currency: </span>
          <span className="text-foreground">{account.currency}</span>
        </div>

        {isOrgOwner && (
          <div className="flex items-center gap-2 pt-3 border-t border-border">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
              Delete
            </Button>
            {!account.isPrimary && (
              <Button variant="ghost" size="sm" onClick={onSetPrimary}>
                <Star aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
                Set Primary
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
